import React from 'react';

import {
    Collapsible,
    CollapsibleHeaderContent,
    PrerequisiteFailureSummary,
    TodoIcon,
    HtmlContent,
} from '@dndbeyond/character-components/es';
import {
    Hack__BaseCharClass,
    ChoiceUtils,
    ClassUtils,
    Constants,
    DataOrigin,
    Feat,
    FeatUtils,
    PrerequisiteData,
    PrerequisiteUtils,
    RaceDefinitionContract,
    RaceUtils,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';

import { RemoveButton, ThemeButton } from '../../../../components/common/Button';
import { DetailChoiceFeat } from '../../../DetailChoice';

interface Props {
    feat: Feat;
    prerequisiteData?: PrerequisiteData;
    onAdd?: (feat: Feat) => void;
    onRemove?: (feat: Feat) => void;
    onDataOriginClick?: (dataOrigin: DataOrigin) => void;
    enableRemove: boolean;
    enableAdd: boolean;
    showFailures: boolean;
    theme: CharacterTheme;
}

export default class FeatsManagePaneFeat extends React.PureComponent<Props, {}> {
    static defaultProps = {
        enableRemove: true,
        enableAdd: true,
        showFailures: false,
    };

    handleDataOriginClick = (evt: React.MouseEvent): void => {
        const { feat, onDataOriginClick } = this.props;

        if (onDataOriginClick) {
            evt.stopPropagation();
            evt.nativeEvent.stopImmediatePropagation();

            let dataOrigin = FeatUtils.getDataOrigin(feat);
            onDataOriginClick(dataOrigin);
        }
    };

    handleRemove = (): void => {
        const { feat, onRemove } = this.props;

        if (onRemove) {
            onRemove(feat);
        }
    };

    handleAdd = (): void => {
        const { feat, onAdd } = this.props;

        if (onAdd) {
            onAdd(feat);
        }
    };

    render() {
        const { feat, enableAdd, enableRemove, showFailures, prerequisiteData, theme } = this.props;

        let choices = FeatUtils.getChoices(feat);
        let missingChoiceCount: number = choices.filter((choice) => ChoiceUtils.isTodo(choice)).length;

        let calloutNode: React.ReactNode = null;
        let dataOrigin = FeatUtils.getDataOrigin(feat);
        let dataOriginType = FeatUtils.getDataOriginType(feat);

        if (dataOriginType === Constants.DataOriginTypeEnum.ADHOC) {
            if (enableRemove) {
                calloutNode = <RemoveButton onClick={this.handleRemove} style="filled" />;
            }
        } else if (dataOriginType === Constants.DataOriginTypeEnum.SIMULATED) {
            if (enableAdd) {
                calloutNode = (
                    <ThemeButton onClick={this.handleAdd} size="small" style="outline">
                        Add
                    </ThemeButton>
                );
            }
        } else {
            //TODO rei - refactor to use EntityUtils.getDataOriginName for logic here
            let dataOriginExtra: React.ReactNode = null;
            switch (dataOriginType) {
                case Constants.DataOriginTypeEnum.CLASS_FEATURE:
                    dataOriginExtra = ClassUtils.getName(dataOrigin.parent as Hack__BaseCharClass);
                    break;

                case Constants.DataOriginTypeEnum.RACE:
                    dataOriginExtra = (
                        <span
                            className="ct-feats-manage-pane__feat-data-origin-name"
                            onClick={this.handleDataOriginClick}
                        >
                            {RaceUtils.getFullName(dataOrigin.parent as RaceDefinitionContract)}
                        </span>
                    );
                    break;

                default:
                    dataOriginExtra = 'Unknown';
            }
            calloutNode = (
                <span className="ct-feats-manage-pane__feat-data-origin">
                    <span className="ct-feats-manage-pane__feat-data-origin-label">From</span>
                    <span className="ct-feats-manage-pane__feat-data-origin-extra">{dataOriginExtra}</span>
                </span>
            );
        }

        let failuresNode: React.ReactNode = null;
        if (showFailures && prerequisiteData) {
            let failures = PrerequisiteUtils.getPrerequisiteGroupingFailures(
                FeatUtils.getPrerequisites(feat),
                prerequisiteData,
            );
            failuresNode = (
                <div className="ct-feats-manage-pane__feat-heading-failures">
                    <PrerequisiteFailureSummary failures={failures} />
                </div>
            );
        }

        let headingNode: React.ReactNode = (
            <div className="ct-feats-manage-pane__feat-heading">
                <div className="ct-feats-manage-pane__feat-heading-primary">
                    <span className="ct-feats-manage-pane__feat-heading-name">{FeatUtils.getName(feat)}</span>
                    {missingChoiceCount > 0 && (
                        <TodoIcon theme={theme} title={`Choice${missingChoiceCount !== 1 ? 's' : ''} Needed`} />
                    )}
                </div>
                {failuresNode}
            </div>
        );

        let headerNode: React.ReactNode = <CollapsibleHeaderContent heading={headingNode} callout={calloutNode} />;

        let prerequisiteDescription = FeatUtils.getPrerequisiteDescription(feat);
        let featDescription = FeatUtils.getDescription(feat);

        return (
            <Collapsible layoutType={'minimal'} className="ct-feats-manage-pane__feat" header={headerNode}>
                <div className="ct-feats-manage-pane__feat-content">
                    {prerequisiteDescription && (
                        <div className="ct-feats-manage-pane__feat-prerequisite">
                            Prerequisite: {prerequisiteDescription}
                        </div>
                    )}
                    {featDescription && (
                        <HtmlContent className="ct-feats-manage-pane__feat-description" html={featDescription} />
                    )}
                    {choices.length > 0 && (
                        <div className="ct-feats-manage-pane__feat-choices">
                            <DetailChoiceFeat featId={FeatUtils.getId(feat)} />
                        </div>
                    )}
                </div>
            </Collapsible>
        );
    }
}
